import MainTable from '@Components/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import { WrappedTableCell } from '@Components/UI/Tables/Tables.styled';
import { egp } from '@ContentManagement/EnrollmentInvoices';
import { TableHeader } from '@Features/generics/generics.type';
import usePlatformMode from '@Hooks/usePlatformMode';
import { Box, Icon, TableCell, TableRow } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import DistributionDonuts from '../Components/DistributionDonuts';
import HubDateRange, { HubRange } from '../Components/HubDateRange';
import { RevenueSummaryCards } from '../Components/SummaryCards';
import WrappedHeadsTable from '../Components/WrappedHeadsTable';
import { analyticsSeasonStart } from '../Constants/methods';
import {
	downloadCsvResponse,
	InstructorsStatisticsRequest,
	OverviewRequest,
} from '../Services/Analytics.req';
import { InstructorsRow, OverviewData, PaginationMeta } from '../Types/Analytics.types';

/**
 * Cross-instructor roll-up. Rows drill into /analytics/instructors/:id —
 * the same scoped endpoints the instructor portal itself will consume.
 */
const InstructorsTab: React.FC = () => {
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { classroomsEnabled, standaloneEnabled, bundlesEnabled } = usePlatformMode();
	const [rows, setRows] = useState<InstructorsRow[]>([]);
	const [meta, setMeta] = useState<PaginationMeta | null>(null);
	const [totals, setTotals] = useState<{
		enrollments: number;
		collected: number;
		total_revenue: number;
	} | null>(null);
	const [topEnrollments, setTopEnrollments] = useState<InstructorsRow[]>([]);
	const [topCollected, setTopCollected] = useState<InstructorsRow[]>([]);
	const [overview, setOverview] = useState<OverviewData | null>(null);
	const seasonRange = useMemo(() => ({ from: analyticsSeasonStart() }), []);
	const [range, setRange] = useState<HubRange>(seasonRange);
	const lastArgsRef = useRef<Partial<onPageChangeRequestMeta>>({});

	const heads = useMemo<TableHeader[]>(() => {
		const list: TableHeader[] = [
			{ displayName: 'Instructor', fieldName: 'instructor_name', canSort: true, width: '18%' },
		];
		if (classroomsEnabled)
			list.push({ displayName: 'Classrooms', fieldName: 'classrooms_count', canSort: true });
		if (standaloneEnabled)
			list.push({ displayName: 'Courses', fieldName: 'standalone_count', canSort: true });
		if (bundlesEnabled)
			list.push({ displayName: 'Bundles', fieldName: 'bundles_count', canSort: true });
		list.push({ displayName: 'Enrollments', fieldName: 'enrollments', canSort: true });
		list.push({ displayName: 'Wallet Revenue', fieldName: 'collected', canSort: true });
		list.push({ displayName: 'Scratchcards Revenue', fieldName: 'scratchcard_value', canSort: true });
		list.push({ displayName: 'Total Revenue', fieldName: 'total_revenue', canSort: true });
		list.push({ displayName: '% of Revenue', fieldName: 'revenue_share', canSort: false });
		return list;
	}, [classroomsEnabled, standaloneEnabled, bundlesEnabled]);

	// KPI cards on top, sharing the table's date range.
	const loadOverviewCards = useCallback(async (from?: Date, to?: Date) => {
		try {
			const response = await OverviewRequest({ from, to });
			setOverview(response.data.data || null);
		} catch {
			/* the table below reports errors */
		}
	}, []);

	const load = useCallback(
		async (request: Partial<onPageChangeRequestMeta> = {}) => {
			lastArgsRef.current = request;
			try {
				const response = await InstructorsStatisticsRequest({
					page: request.activePage || 1,
					perPage: request.perPage || 10,
					query: request.query || '',
					sortBy: request.sortBy?.field ? request.sortBy : undefined,
					from: range.from,
					to: range.to,
				});
				const data = response.data.data || {};
				setRows(data.rows || []);
				setTotals(data.totals || null);
				setMeta(response.data.meta || null);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load instructors statistics'
				);
			}
		},
		[displaySnackbar, range]
	);

	// Two small top-8 requests feed the donuts; "Other" comes from the totals.
	const loadDonuts = useCallback(async (from?: Date, to?: Date) => {
		try {
			const [byEnrollments, byCollected] = await Promise.all([
				InstructorsStatisticsRequest({
					page: 1,
					perPage: 8,
					from,
					to,
					sortBy: { field: 'enrollments', direction: 'desc' },
				}),
				InstructorsStatisticsRequest({
					page: 1,
					perPage: 8,
					from,
					to,
					sortBy: { field: 'total_revenue', direction: 'desc' },
				}),
			]);
			setTopEnrollments(byEnrollments.data.data?.rows || []);
			setTopCollected(byCollected.data.data?.rows || []);
		} catch {
			/* donuts are decorative — the table already reports errors */
		}
	}, []);

	// The tab's HubDateRange drives the cards, donuts and table together.
	useEffect(() => {
		load({ activePage: 1, perPage: 10 });
		loadDonuts(range.from, range.to);
		loadOverviewCards(range.from, range.to);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [range]);

	const withOther = (top: InstructorsRow[], metric: 'enrollments' | 'total_revenue') => {
		const slices = top.map((row) => ({
			id: row.instructor_id,
			name: row.instructor_name || `#${row.instructor_id}`,
			value: Number(row[metric] || 0),
		}));
		const covered = slices.reduce((sum, slice) => sum + slice.value, 0);
		const total = Number(totals?.[metric] ?? 0);
		if (total - covered > 0) slices.push({ id: 0, name: 'Other', value: total - covered });
		return slices;
	};

	const exportRows = async () => {
		const request = lastArgsRef.current;
		try {
			const response = await InstructorsStatisticsRequest({
				query: request.query || '',
				sortBy: request.sortBy?.field ? request.sortBy : undefined,
				from: range.from,
				to: range.to,
				export: true,
			});
			downloadCsvResponse(response, 'instructors-statistics');
		} catch {
			displaySnackbar('error', 'Unable to export');
		}
	};

	return (
		<TabPage>
			<Box display="flex" justifyContent="flex-end" alignItems="center" gap="1rem" mb="1rem">
				<HubDateRange initialRange={seasonRange} onApply={(nextRange) => setRange(nextRange)} />
				<RoundButton onClick={exportRows} startIcon={<Icon>south</Icon>} variant="contained">
					Export
				</RoundButton>
			</Box>
			{overview && (
				<RevenueSummaryCards
					showRefundCards={false}
					summary={{
						methods: overview.methods,
						grand_total: overview.revenue.grand_total,
						enrollments: overview.enrollment_summary.purchase_methods_total,
						discounts: overview.revenue.discounts,
						refunded: overview.revenue.refunded,
						refund_count: 0,
						net: overview.revenue.total_revenue,
					}}
				/>
			)}
			<DistributionDonuts
				enrollments={withOther(topEnrollments, 'enrollments')}
				revenue={withOther(topCollected, 'total_revenue')}
				enrollmentsTitle="Enrollments by instructor"
				revenueTitle="Total Revenue by instructor"
			/>
			<WrappedHeadsTable>
			<MainTable
				tableFilters={[]}
				RowsperPage={meta?.per_page || 10}
				onPageChangeRequest={load}
				totalPages={meta?.last_page || 1}
				total={meta?.total || 0}
				pageNum={meta?.current_page || 1}
				tableHeads={heads}
				dateFilter={false}
				tableMinWidth="60rem"
				renderItems={() => {
					if (!rows.length) {
						return (
							<TableRow>
								<TableCell colSpan={heads.length}>
									<NoResults />
								</TableCell>
							</TableRow>
						);
					}
					return rows.map((row) => (
						<TableRow
							key={row.instructor_id}
							hover
							sx={{ cursor: 'pointer' }}
							onClick={() =>
								history.push(`/dashboard/analytics/instructors/${row.instructor_id}`, {
									name: row.instructor_name,
								})
							}
						>
							<WrappedTableCell align="center">
								{row.instructor_name || `#${row.instructor_id}`}
							</WrappedTableCell>
							{classroomsEnabled && (
								<TableCell align="center">{row.classrooms_count}</TableCell>
							)}
							{standaloneEnabled && (
								<TableCell align="center">{row.standalone_count}</TableCell>
							)}
							{bundlesEnabled && <TableCell align="center">{row.bundles_count}</TableCell>}
							<TableCell align="center">{row.enrollments}</TableCell>
							<TableCell align="center">{egp(row.collected)}</TableCell>
							<TableCell align="center">{egp(row.scratchcard_value)}</TableCell>
							<TableCell align="center">{egp(row.total_revenue)}</TableCell>
							<TableCell align="center">
								{totals?.total_revenue
									? `${((row.total_revenue / totals.total_revenue) * 100).toFixed(1)}%`
									: '-'}
							</TableCell>
						</TableRow>
					));
				}}
			/>
			</WrappedHeadsTable>
		</TabPage>
	);
};

const TabPage = styled.div`
	width: 100%;
	max-width: 100%;
	min-width: 0;
`;

export default InstructorsTab;
